import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  X, Loader2, Briefcase, MapPin, Clock, Euro, Calendar,
  Users, Monitor, Building2, FileText, Send
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Work mode options
const WORK_MODES = [
  { value: 'onsite', label: 'Sur site', icon: Building2 },
  { value: 'remote', label: 'Télétravail', icon: Monitor },
  { value: 'hybrid', label: 'Hybride', icon: Users },
];

// Duration options
const DURATIONS = [
  { value: '1_week', label: '1 semaine' },
  { value: '2_weeks', label: '2 semaines' },
  { value: '1_month', label: '1 mois' },
  { value: '2_months', label: '2 mois' },
  { value: '3_months', label: '3 mois' },
  { value: '6_months', label: '6 mois' },
  { value: '1_year', label: '1 an' },
  { value: 'ongoing', label: 'Durée indéterminée' },
];

// Rate types
const RATE_TYPES = [
  { value: 'hourly', label: 'Taux horaire' },
  { value: 'daily', label: 'Taux journalier' },
  { value: 'fixed', label: 'Forfait' },
];

export const MissionCreateModal = ({ isOpen, onClose, onSuccess }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sectors, setSectors] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    required_skills: '',
    sector: '',
    experience_required: '',
    work_mode: 'hybrid',
    location: '',
    duration: '',
    start_date: '',
    budget_min: '',
    budget_max: '',
    rate_type: 'daily',
    application_deadline: '',
  });

  useEffect(() => {
    // Fetch sectors
    fetch(`${API_URL}/api/sectors`)
      .then(res => res.json())
      .then(data => setSectors(data.sectors || []))
      .catch(err => console.error('Error fetching sectors:', err));
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e, publish = false) => {
    e.preventDefault();
    
    // Validation
    if (!formData.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('La description est requise');
      return;
    }
    if (!formData.required_skills.trim()) {
      toast.error('Les compétences requises sont obligatoires');
      return;
    }

    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('sfretire_token');
      
      const payload = {
        ...formData,
        experience_required: formData.experience_required ? parseInt(formData.experience_required) : null,
        budget_min: formData.budget_min ? parseFloat(formData.budget_min) : null,
        budget_max: formData.budget_max ? parseFloat(formData.budget_max) : null,
        publish: publish,
      };

      const response = await fetch(`${API_URL}/api/missions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        toast.success(publish ? 'Mission publiée avec succès !' : 'Mission enregistrée comme brouillon');
        onSuccess && onSuccess(result.mission);
        onClose();
        // Reset form
        setFormData({
          title: '',
          description: '',
          required_skills: '',
          sector: '',
          experience_required: '',
          work_mode: 'hybrid',
          location: '',
          duration: '',
          start_date: '',
          budget_min: '',
          budget_max: '',
          rate_type: 'daily',
          application_deadline: '',
        });
      } else {
        toast.error(result.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Mission creation error:', error);
      toast.error('Erreur de connexion au serveur');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-foreground/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-card rounded-2xl shadow-elevated w-full max-w-2xl my-8 max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Briefcase className="text-primary" size={20} />
            </div>
            <div>
              <h3 className="text-xl font-serif font-semibold text-foreground">
                Nouvelle mission
              </h3>
              <p className="text-sm text-muted-foreground">
                Décrivez la mission pour attirer les meilleurs experts
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-secondary rounded-lg transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={(e) => handleSubmit(e, false)} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Title */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <FileText size={16} className="text-muted-foreground" />
              Titre de la mission *
            </label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Ex: Consultant Finance pour restructuration"
              className="mt-1"
              data-testid="mission-title-input"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-foreground">Description détaillée *</label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Décrivez les objectifs, le contexte et les responsabilités..."
              className="mt-1 min-h-[120px]"
              data-testid="mission-description-input"
            />
          </div>

          {/* Skills */}
          <div>
            <label className="text-sm font-medium text-foreground">Compétences requises *</label>
            <Input
              name="required_skills"
              value={formData.required_skills}
              onChange={handleChange}
              placeholder="Ex: Finance, Excel, SAP, Gestion de projet (séparées par des virgules)"
              className="mt-1"
              data-testid="mission-skills-input"
            />
            <p className="text-xs text-muted-foreground mt-1">Séparez les compétences par des virgules</p>
          </div>

          {/* Two columns */}
          <div className="grid sm:grid-cols-2 gap-4">
            {/* Sector */}
            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Building2 size={16} className="text-muted-foreground" />
                Secteur
              </label>
              <select
                name="sector"
                value={formData.sector}
                onChange={handleChange}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                data-testid="mission-sector-select"
              >
                <option value="">Sélectionner un secteur</option>
                {sectors.map(sector => (
                  <option key={sector.id} value={sector.id}>
                    {sector.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Experience */}
            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Users size={16} className="text-muted-foreground" />
                Expérience minimum (années)
              </label>
              <Input
                type="number"
                name="experience_required"
                value={formData.experience_required}
                onChange={handleChange}
                placeholder="Ex: 10"
                min="0"
                className="mt-1"
              />
            </div>
          </div>

          {/* Work Mode */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
              <Monitor size={16} className="text-muted-foreground" />
              Mode de travail
            </label>
            <div className="flex gap-2">
              {WORK_MODES.map(mode => (
                <button
                  key={mode.value}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, work_mode: mode.value }))}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border transition-all ${
                    formData.work_mode === mode.value
                      ? 'border-primary bg-primary/10 text-primary'
                      : 'border-border hover:border-primary/50'
                  }`}
                >
                  <mode.icon size={16} />
                  <span className="text-sm font-medium">{mode.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Location & Duration */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <MapPin size={16} className="text-muted-foreground" />
                Localisation
              </label>
              <Input
                name="location"
                value={formData.location}
                onChange={handleChange}
                placeholder="Ex: Paris, Lyon, Télétravail..."
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Clock size={16} className="text-muted-foreground" />
                Durée estimée
              </label>
              <select
                name="duration"
                value={formData.duration}
                onChange={handleChange}
                className="mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Sélectionner une durée</option>
                {DURATIONS.map(d => (
                  <option key={d.value} value={d.value}>{d.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Dates */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Calendar size={16} className="text-muted-foreground" />
                Date de début souhaitée
              </label>
              <Input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleChange}
                className="mt-1"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground flex items-center gap-2">
                <Calendar size={16} className="text-muted-foreground" />
                Date limite de candidature
              </label>
              <Input
                type="date"
                name="application_deadline"
                value={formData.application_deadline}
                onChange={handleChange}
                className="mt-1"
              />
            </div>
          </div>

          {/* Budget */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2 mb-2">
              <Euro size={16} className="text-muted-foreground" />
              Budget
            </label>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <select
                  name="rate_type"
                  value={formData.rate_type}
                  onChange={handleChange}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  {RATE_TYPES.map(rt => (
                    <option key={rt.value} value={rt.value}>{rt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <Input
                  type="number"
                  name="budget_min"
                  value={formData.budget_min}
                  onChange={handleChange}
                  placeholder="Min (€)"
                  min="0"
                />
              </div>
              <div>
                <Input
                  type="number"
                  name="budget_max"
                  value={formData.budget_max}
                  onChange={handleChange}
                  placeholder="Max (€)"
                  min="0"
                />
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-border bg-secondary/30">
          <Button
            type="button"
            variant="outline"
            onClick={(e) => handleSubmit(e, false)}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <FileText className="mr-2" size={16} />
            )}
            Enregistrer brouillon
          </Button>
          <Button
            type="button"
            variant="gold"
            onClick={(e) => handleSubmit(e, true)}
            disabled={isSubmitting}
            className="flex-1"
          >
            {isSubmitting ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Send className="mr-2" size={16} />
            )}
            Publier la mission
          </Button>
        </div>
      </motion.div>
    </div>
  );
};

export default MissionCreateModal;
